import React, { useEffect, useRef, useState } from "react";
import type { ComponentProps } from "react";
import { BarVisualizer, useRoomContext } from "@livekit/components-react";
import { ConnectionState } from "livekit-client";
import { CallButton } from "../CallButton/CallButton";
import { USER_ACTIVITY_TOPIC } from "../constants";
import { SendSvg } from "../icons";

type VisualizerProps = ComponentProps<typeof BarVisualizer>;

// iOS only raises the soft keyboard from inside a real user gesture.
// Programmatically focusing the textarea gives it a caret but NO keyboard, and
// because the field is then already `:focus`, the user's subsequent tap fires no
// focus event so the keyboard never appears — fine on Android (which opens the
// keyboard on programmatic focus), dead on iPhone. So we only auto-focus on
// precise-pointer (desktop) devices; on touch, the user's tap opens the keyboard
// and submit()'s in-gesture re-focus keeps it up across a send.
const PREFERS_AUTOFOCUS =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: fine)").matches;

export function MessageInput({
  onSubmit,
  onToggleVoice,
  voiceActive,
  voiceConnecting,
  isSending,
  showVisualizer,
  agentState,
  microphoneTrack,
}: {
  onSubmit: (text: string) => Promise<void>;
  onToggleVoice: () => void;
  voiceActive: boolean;
  voiceConnecting: boolean;
  isSending: boolean;
  showVisualizer?: boolean;
  agentState?: VisualizerProps["state"];
  microphoneTrack?: VisualizerProps["track"];
}) {
  const [input, setInput] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const room = useRoomContext();
  const lastChatActiveRef = useRef<boolean>(false);

  useEffect(() => {
    // Acquire focus only on desktop — see PREFERS_AUTOFOCUS. On touch this would
    // leave the field focused-but-keyboardless on iOS and swallow the next tap.
    if (!PREFERS_AUTOFOCUS) return;
    if (!isSending) {
      textareaRef.current?.focus();
    }
  }, [isSending]);

  // Tell the agent when the composer goes empty <-> non-empty. The agent uses
  // this to suppress the voice idle watchdog while the user is typing — so
  // pausing to type a chat reply doesn't get cut off by an "are you still
  // there?" check-in or hangup.
  const isComposing = input.length > 0;
  useEffect(() => {
    if (!room?.localParticipant) return;
    // Nothing to tell before a session exists (the empty chat is typeable).
    if (room.state !== ConnectionState.Connected) return;
    if (lastChatActiveRef.current === isComposing) return;
    lastChatActiveRef.current = isComposing;
    const payload = new TextEncoder().encode(
      JSON.stringify({ event: "chat_active", active: isComposing }),
    );
    void room.localParticipant
      .publishData(payload, { reliable: true, topic: USER_ACTIVITY_TOPIC })
      .catch(() => {
        // Best-effort: may race with connect/disconnect. State recovers on
        // the next transition.
      });
  }, [room, isComposing]);

  const submit = async () => {
    const text = input.trim();
    // Sending while the agent is still connecting is fine: the panel queues it.
    if (!text || isSending) return;
    setInput("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "";
      textareaRef.current.style.overflowY = "hidden";
      // Re-assert focus *synchronously, inside the send gesture* (click/keydown).
      // iOS only keeps/opens the soft keyboard from within a real user gesture;
      // the post-send focus effect runs after the await, outside the gesture, so
      // iOS ignores it and the keyboard drops. Calling focus() here — before the
      // await — keeps the keyboard up on send.
      textareaRef.current.focus();
    }
    try {
      await onSubmit(text);
    } catch {
      setInput(text);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submit();
  };

  return (
    <form className="lk-widget-input" onSubmit={handleSubmit}>
      <textarea
        ref={textareaRef}
        rows={1}
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          const el = e.target;
          el.style.height = "auto";
          const next = Math.min(el.scrollHeight, 123);
          el.style.height = next + "px";
          el.style.overflowY = el.scrollHeight > 123 ? "auto" : "hidden";
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void submit();
          }
        }}
        placeholder="Write something"
        // NEVER disable or readOnly this field, not even while the agent
        // connects. iOS shows no keyboard for a tap on a readOnly field, and
        // when readOnly later drops the field is already focused, so no focus
        // event fires and the keyboard stays down (2026-09-29).
        autoFocus={PREFERS_AUTOFOCUS}
        autoComplete="off"
      />
      <div className="lk-widget-input-toolbar">
        <CallButton
          onClick={onToggleVoice}
          active={voiceActive}
          connecting={voiceConnecting}
          size="sm"
          showVisualizer={showVisualizer}
          agentState={agentState}
          microphoneTrack={microphoneTrack}
        />
        <button
          type="submit"
          className="lk-widget-send-btn"
          // Don't let the button steal focus from the textarea on press — that
          // blurs the input and drops the soft keyboard on every send (the
          // "flick"). preventDefault on mousedown keeps focus in the composer;
          // the click still fires and submits the form.
          onMouseDown={(e) => e.preventDefault()}
          disabled={!input.trim() || isSending}
          aria-label="Send message"
        >
          {SendSvg}
        </button>
      </div>
    </form>
  );
}
